// Europe/Berlin wall-clock helpers built on Intl (no date library in this repo).

const BERLIN_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Berlin",
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

// Berlin's offset from UTC at `instant`, in ms (+1h in winter, +2h in summer).
const berlinOffsetMs = (instant: number): number => {
  const parts = BERLIN_PARTS.formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return wallClockAsUtc - Math.floor(instant / 1000) * 1000;
};

/**
 * 23:59:59 Berlin time on the given calendar day, as an instant.
 *
 * The offset is looked up for that day, so DST is handled: 31 Dec gives 22:59:59Z, 31 Jul gives
 * 21:59:59Z. 23:59:59 never falls in a DST gap (Berlin switches at 02:00/03:00).
 */
export const endOfBerlinDay = (year: number, month: number, day: number): Date => {
  const wallClockAsUtc = Date.UTC(year, month - 1, day, 23, 59, 59);
  // First guess uses the offset at the UTC reading of the wall clock; taking the offset again at
  // that guess corrects it when the real instant lands on the other side of a switch.
  const guess = wallClockAsUtc - berlinOffsetMs(wallClockAsUtc);
  return new Date(wallClockAsUtc - berlinOffsetMs(guess));
};

/** endOfBerlinDay for the calendar day a date picker selected (its local Y/M/D). */
export const endOfBerlinDayFromPicked = (picked: Date): Date =>
  endOfBerlinDay(picked.getFullYear(), picked.getMonth() + 1, picked.getDate());
